import React, { useEffect, useState } from 'react';
import { Box, Table, Tbody, Td, Text, Thead, Tr, useToast } from '@chakra-ui/react';
import { InlineLoader, SortableTh, StatusBadge } from '../../../components/ui';
import {
  actionBadgeStatus,
  actionTypeLabel,
} from '../../../data/staff/activityLogsStore';
import { useTableSort } from '../../../hooks/useTableSort';
import { listActivityLogs } from '../../../repositories/activityLog.repository';

function formatLogTime(at) {
  if (!at) return '—';
  return String(at).slice(0, 16).replace('T', ' ');
}

export function StaffActivityTable({
  rows: rowsProp,
  staffId,
  showStaffColumn = false,
  emptyMessage,
}) {
  const toast = useToast();
  const [loadedRows, setLoadedRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const controlled = rowsProp != null;
  const rows = controlled ? rowsProp : loadedRows;

  useEffect(() => {
    if (controlled || staffId == null || staffId === '') return undefined;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await listActivityLogs({
          staffId,
          page: 1,
          pageSize: 200,
        });
        if (!cancelled) setLoadedRows(result.items || []);
      } catch (err) {
        if (!cancelled) {
          setLoadedRows([]);
          toast({
            title: err?.message || 'Failed to load activity logs',
            status: 'error',
            position: 'top-right',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [controlled, staffId, toast]);

  const { sorted, sort, toggleSort } = useTableSort(
    rows,
    {
      at: (l) => l.at,
      staff: (l) => l.staffName,
      action: (l) => actionTypeLabel(l.actionType),
      module: (l) => l.module,
      summary: (l) => l.summary,
      entityId: (l) => l.entityId,
    },
    { key: 'at', dir: 'desc' },
  );

  const displayEmpty = emptyMessage || 'No activity recorded yet.';

  if (loading) {
    return <InlineLoader label="Loading activity…" py={10} />;
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="md">
        <Thead bg="bg.input">
          <Tr>
            <SortableTh columnKey="at" sort={sort} onSort={toggleSort}>
              When
            </SortableTh>
            {showStaffColumn ? (
              <SortableTh columnKey="staff" sort={sort} onSort={toggleSort}>
                Staff
              </SortableTh>
            ) : null}
            <SortableTh columnKey="action" sort={sort} onSort={toggleSort}>
              Action
            </SortableTh>
            <SortableTh columnKey="module" sort={sort} onSort={toggleSort}>
              Module
            </SortableTh>
            <SortableTh columnKey="summary" sort={sort} onSort={toggleSort}>
              Details
            </SortableTh>
            <SortableTh columnKey="entityId" sort={sort} onSort={toggleSort}>
              Reference
            </SortableTh>
          </Tr>
        </Thead>
        <Tbody>
          {sorted.map((log) => (
            <Tr key={log.logId || log.id} _hover={{ bg: 'bg.input' }}>
              <Td fontSize="12px" color="text.secondary" whiteSpace="nowrap">
                {formatLogTime(log.at)}
              </Td>
              {showStaffColumn ? (
                <Td>
                  <Text fontWeight="600" fontSize="13px">
                    {log.staffName}
                  </Text>
                  <Text fontSize="12px" color="text.secondary" fontFamily="mono">
                    {log.staffId}
                  </Text>
                </Td>
              ) : null}
              <Td>
                <StatusBadge status={actionBadgeStatus(log.actionType)}>
                  {actionTypeLabel(log.actionType)}
                </StatusBadge>
              </Td>
              <Td fontSize="13px">{log.module}</Td>
              <Td fontSize="13px" maxW="320px">
                {log.summary}
              </Td>
              <Td fontFamily="mono" fontSize="12px" color="text.secondary">
                {log.entityId || '—'}
              </Td>
            </Tr>
          ))}
          {sorted.length === 0 ? (
            <Tr>
              <Td colSpan={showStaffColumn ? 6 : 5}>
                <Text color="text.secondary" py={8} textAlign="center">
                  {displayEmpty}
                </Text>
              </Td>
            </Tr>
          ) : null}
        </Tbody>
      </Table>
    </Box>
  );
}
